import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatRowDef,
  MatRow,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { CatalogSearchService } from '@services/catalog-search/1.0/catalog-search.service';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MessageType } from '@constants/message/1.0';
import { CatalogSearchItem, CatalogSearchItemType } from '@interfaces/catalog-search/1.0';
import { filter } from 'rxjs/operators';
import { BrandService } from '@services/brand/1.0/brand.service';
import { StoreResolverService } from '@app/store/services/store-resolver.service';
import { Tenant } from '@generated/tcea/models/tenant';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog } from '@angular/material/dialog';
import { SelectBulkStoresDialogComponent } from '@components/select-bulk-stores/1.0/select-bulk-stores-dialog.component';
import { CatalogEntry } from '@generated/tcea/models/catalog-entry';
import { NgIf, NgFor } from '@angular/common';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MessageComponent } from '../../message/1.0/message.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'txp-search-catalog',
  templateUrl: './search-catalog-dialog.component.html',
  styleUrls: ['./search-catalog-dialog.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    NgFor,
    MatOption,
    ButtonComponent,
    MessageComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    MatRowDef,
    MatRow,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
  ],
})
export class SearchCatalogComponent implements AfterViewInit, OnInit {
  @ViewChild(MatSort) sort: MatSort;

  brand: Tenant;
  storeId?: string;
  dataSource: MatTableDataSource<CatalogSearchItem> = new MatTableDataSource<CatalogSearchItem>();
  displayColumns = ['select', 'name', 'type', 'plu', 'actions'];
  buttonStyle = ButtonStyle;
  buttonSize = ButtonSize;
  selection = new SelectionModel<CatalogSearchItem>(true, []);
  catalogTypes = [
    { value: 'all', label: 'catalog.searchModal.types.all' },
    { value: CatalogSearchItemType.PRODUCT, label: 'catalog.searchModal.types.product' },
    { value: CatalogSearchItemType.COMPOSITE, label: 'catalog.searchModal.types.combo' },
    { value: CatalogSearchItemType.MODIFIER, label: 'catalog.searchModal.types.modifier' },
  ];
  defaultFormValue: any;
  messageType = MessageType; // expose enum to template
  showError = false;
  searchDone = false;
  form: UntypedFormGroup;
  isLoading = false;

  // Parent expansion state
  expandedItems = new Set<string>();
  parentItems = new Map<string, CatalogEntry[]>();
  loadingParents = new Set<string>();
  selectedParents = new Set<string>();
  selectedParentNames = new Map<string, string>();

  constructor(
    private brandService: BrandService,
    private formBuilder: UntypedFormBuilder,
    private catalogSearchService: CatalogSearchService,
    private router: Router,
    private dialog: MatDialog,
    private storeResolverService: StoreResolverService,
  ) {
    this.form = this.formBuilder.group({
      searchInput: new UntypedFormControl(''),
      plu: new UntypedFormControl(''),
      itemType: new UntypedFormControl(this.catalogTypes[0]),
    });
    this.defaultFormValue = this.form.value;
  }

  ngOnInit() {
    // pick up active store if inside stores flow
    this.storeResolverService.storeSubject
      .pipe(filter((s) => !!s))
      .subscribe((s) => (this.storeId = s.id));

    this.brandService.currentBrandBehaviorSubject
      .pipe(filter((brand) => brand.isSet))
      .subscribe((brand) => {
        this.brand = brand;

        const cachedParams = this.catalogSearchService.cachedParams;

        if (cachedParams) {
          const itemTypeSelected =
            this.catalogTypes.find((type) => type.value === cachedParams.itemType) ||
            this.catalogTypes[0];

          this.form.patchValue({
            plu: cachedParams.plu,
            searchInput: cachedParams.query,
            itemType: itemTypeSelected,
          });
          this.handleSearch();
        }
      });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  goToItem(item: CatalogSearchItem) {
    const ITEM_TYPE_MAP = {
      [CatalogSearchItemType.PRODUCT]: 'products',
      [CatalogSearchItemType.COMPOSITE]: 'combos',
      [CatalogSearchItemType.COMBO]: 'combos',
      [CatalogSearchItemType.MODIFIER]: 'modifiers',
    } as const;

    if (item?.catalogEntryId && ITEM_TYPE_MAP[item.itemType]) {
      this.router.navigateByUrl(`/catalog/${ITEM_TYPE_MAP[item.itemType]}/${item.catalogEntryId}`);
    }
  }

  goToParentItem(item: CatalogEntry) {
    if (!item?.catalogEntryId || !item.entityType) {
      return;
    }

    let catalogPath: string | null = null;

    switch (item.entityType) {
      case 'PRODUCT':
        catalogPath = 'products';
        break;
      case 'MODIFIER':
        catalogPath = 'modifiers';
        break;
      case 'COMPOSITE':
        // Handle composites based on compositeType
        if (item.compositeType === 'CONTAINER') {
          catalogPath = 'containers';
        } else if (item.compositeType === 'COMBO' || item.compositeType === 'BUNDLE') {
          catalogPath = 'combos';
        }
        break;
    }

    if (catalogPath) {
      this.router.navigateByUrl(`/catalog/${catalogPath}/${item.catalogEntryId}`);
    }
  }

  searchEnabled(): boolean {
    const { searchInput, itemType, plu } = this.form.value;
    return Boolean(searchInput || itemType?.value !== 'all' || plu);
  }

  handleReset() {
    this.form.reset(this.defaultFormValue);
    this.catalogSearchService.resetMasterCache();
    this.catalogSearchService.resetParams();
    this.expandedItems.clear();
    this.parentItems.clear();
    this.loadingParents.clear();
    this.dataSource.data = [...this.selection.selected];
    this.searchDone = this.selection.selected.length > 0;
  }

  isAllSelected() {
    const numRows = this.dataSource.data.length;
    if (numRows === 0) {
      return false;
    }
    // Only count selections that are actually in the current dataSource
    const currentDataIds = new Set(this.dataSource.data.map((item) => item.catalogEntryId));
    const numSelected = this.selection.selected.filter((item) =>
      currentDataIds.has(item.catalogEntryId),
    ).length;
    return numSelected === numRows;
  }

  masterToggle() {
    if (this.isAllSelected()) {
      const currentDataIds = new Set(this.dataSource.data.map((item) => item.catalogEntryId));
      const remainingSelections = this.selection.selected.filter(
        (item) => !currentDataIds.has(item.catalogEntryId),
      );
      this.selection = new SelectionModel<CatalogSearchItem>(true, remainingSelections);
    } else {
      const selectedById = this.selection.selected.reduce((acc, item) => {
        acc.set(item.catalogEntryId, item);
        return acc;
      }, new Map<string, CatalogSearchItem>());

      for (const row of this.dataSource.data) {
        selectedById.set(row.catalogEntryId, row);
      }

      this.selection = new SelectionModel<CatalogSearchItem>(
        true,
        Array.from(selectedById.values()),
      );
    }
  }

  handleSearch() {
    const formattedData = Object.keys(this.form.value).reduce((obj, key) => {
      if (this.form.value[key] && this.form.value[key]?.value !== 'all') {
        obj[key] = this.form.value[key];
      }
      return obj;
    }, {}) as any;
    const getTitle = (item) =>
      item.title ? item.title : (item.decorators?.title || {})[this.brand.language];
    const getInternalName = (item) => item.name || item.internalName || getTitle(item) || '';

    // Keep current selections across searches, but clear expanded/loaded parent rows tied to current view.
    this.expandedItems.clear();
    this.parentItems.clear();
    this.loadingParents.clear();

    this.isLoading = true;
    const store = this.storeResolverService.storeSubject.getValue();
    const dataGroupId = store?.dataGroupId;
    this.catalogSearchService
      .search(this.brand.id, {
        plu: formattedData.plu,
        itemType: formattedData.itemType?.value,
        query: formattedData.searchInput,
        dataGroupId,
      })
      .subscribe({
        next: (catalogItems) => {
          const BACKEND_TYPE_MAP = {
            [CatalogSearchItemType.COMPOSITE]: CatalogSearchItemType.COMBO,
          } as const;
          const mappedCatalogItems = catalogItems.map((item) => ({
            ...item,
            itemType: BACKEND_TYPE_MAP[item.itemType] || item.itemType,
            name: getInternalName(item),
            plu: item.plu,
          }));
          const selectedById = this.selection.selected.reduce((acc, item) => {
            acc.set(item.catalogEntryId, item);
            return acc;
          }, new Map<string, CatalogSearchItem>());

          for (const row of mappedCatalogItems) {
            if (selectedById.has(row.catalogEntryId)) {
              selectedById.set(row.catalogEntryId, row);
            }
          }

          this.selection = new SelectionModel<CatalogSearchItem>(
            true,
            Array.from(selectedById.values()),
          );
          this.dataSource.data = mappedCatalogItems;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = true;
          this.showError = true;
        },
        complete: () => {
          this.searchDone = true;
        },
      });
  }

  toggleParentExpansion(item: CatalogSearchItem) {
    const catalogEntryId = item.catalogEntryId;

    if (this.expandedItems.has(catalogEntryId)) {
      // Collapse
      this.expandedItems.delete(catalogEntryId);
      this.parentItems.delete(catalogEntryId);
    } else {
      // Expand
      this.expandedItems.add(catalogEntryId);
      this.loadParents(item);
    }
  }

  isExpanded(item: CatalogSearchItem): boolean {
    return this.expandedItems.has(item.catalogEntryId);
  }

  isLoadingParents(item: CatalogSearchItem): boolean {
    return this.loadingParents.has(item.catalogEntryId);
  }

  getParentItems(item: CatalogSearchItem): CatalogEntry[] {
    return this.parentItems.get(item.catalogEntryId) || [];
  }

  private loadParents(item: CatalogSearchItem) {
    this.loadingParents.add(item.catalogEntryId);
    this.catalogSearchService.getParents(item.catalogEntryId).subscribe({
      next: (response) => {
        const allParents = response._embedded?.catalogEntries || [];
        // Filter out the current item and groups (PRODUCT_GROUP, MODIFIER_GROUP) from the parent list
        const parents = allParents.filter(
          (parent) =>
            parent.catalogEntryId !== item.catalogEntryId &&
            parent.entityType !== 'PRODUCT_GROUP' &&
            parent.entityType !== 'MODIFIER_GROUP',
        );
        this.parentItems.set(item.catalogEntryId, parents);
        this.loadingParents.delete(item.catalogEntryId);
      },
      error: (error) => {
        this.loadingParents.delete(item.catalogEntryId);
      },
    });
  }

  // Helper method to get all items (including parents) for selection
  getAllItemsForSelection(): (CatalogSearchItem | CatalogEntry)[] {
    const allItems: (CatalogSearchItem | CatalogEntry)[] = [];

    this.dataSource.data.forEach((item) => {
      allItems.push(item);
      if (this.isExpanded(item)) {
        const parents = this.getParentItems(item);
        allItems.push(...parents);
      }
    });

    return allItems;
  }

  toggleParentSelection(parentItem: CatalogEntry) {
    const parentId = parentItem.catalogEntryId;
    if (this.selectedParents.has(parentId)) {
      this.selectedParents.delete(parentId);
      this.selectedParentNames.delete(parentId);
    } else {
      this.selectedParents.add(parentId);
      this.selectedParentNames.set(parentId, parentItem.title || parentItem.name || parentId);
    }
  }

  isParentSelected(parentItem: CatalogEntry): boolean {
    return this.selectedParents.has(parentItem.catalogEntryId);
  }

  // Override the openUpdateAvailabilityDialog to include parent items
  openUpdateAvailabilityDialog() {
    const selectedCatalogEntryIds = this.selection.selected.map((item) => item.catalogEntryId);
    const selectedCatalogEntryNames = this.selection.selected.map(
      (item) => item.name || item.title,
    );

    // Add selected parent items
    const selectedParentIds = Array.from(this.selectedParents);
    const selectedParentNames = selectedParentIds.map((id) => {
      // Find parent item name from all parent items
      for (const parents of this.parentItems.values()) {
        const parent = parents.find((p) => p.catalogEntryId === id);
        if (parent) {
          return parent.title || parent.name || id;
        }
      }
      return this.selectedParentNames.get(id) || id;
    });

    // Combine all selected items
    const allSelectedIds = [...selectedCatalogEntryIds, ...selectedParentIds];
    const allSelectedNames = [...selectedCatalogEntryNames, ...selectedParentNames];

    // Build dialog data, only including storeId if it's defined
    const dialogData: {
      brandId: string;
      catalogEntryIds: string[];
      catalogEntryNames: string[];
      storeId?: string;
    } = {
      brandId: this.brand.id,
      catalogEntryIds: allSelectedIds,
      catalogEntryNames: allSelectedNames,
    };

    if (this.storeId) {
      dialogData.storeId = this.storeId;
    }

    this.dialog.open(SelectBulkStoresDialogComponent, {
      data: dialogData,
    });
  }
}
